import {
  attachmentId,
  pixelBufferType,
  primitiveType,
  RBIntFormat,
  stencilFunc,
  stencilValue
} from "_src/engine/constants";

import Pass from "_src/render_graph/pass";
import FrameBuffer from "_src/engine/framebuffer";
import Camera from "_src/engine/camera";
import CopyShader from "_src/shaders/copy/copy";
import objFactory from "_src/engine/factories/object";
import primitiveFactory from "_src/engine/factories/primitive";
import resourceManager from "_src/render_graph/resource_manager";
import GL from "_src/engine/gl";
import ScreenShader from "_src/shaders/screen/screen";
import {screenUtils} from "_src/client/utils";
import OutlineShader from "_src/shaders/outline/outline";

const OUTLINE_COLOR = [1, 1, 1];
const OPAQUE_OUTLINE_TRANSPARENCY = 1;
const TRANSPARENT_OUTLINE_TRANSPARENCY = 0.5;

export default class MeasureLinesPass extends Pass {

  constructor(attachmentsStorage, camera) {

    super(attachmentsStorage);

    this.init(camera);

  }

  init(camera) {
    const screenCamera = new Camera();

    const opaqueLinesScene = this._addScene(camera);
    const transparentLinesScene = this._addScene(camera);
    const opaqueMaskScene = this._addScene(screenCamera);
    const transparentMaskScene = this._addScene(screenCamera);
    const outlineScene = this._addScene(screenCamera);
    const blendingScene = this._addScene(screenCamera);
    const textScene = this._addScene(camera);

    opaqueLinesScene.clearedColor = true;
    opaqueLinesScene.clearedDepth = true;
    opaqueLinesScene.clearedStencil = true;

    transparentLinesScene.clearedColor = false;
    transparentLinesScene.clearedDepth = true;
    transparentLinesScene.clearedStencil = false;
    transparentLinesScene.isPostBlending = true;

    opaqueMaskScene.clearedColor = true;
    opaqueMaskScene.clearedDepth = false;
    opaqueMaskScene.clearedStencil = false;

    transparentMaskScene.clearedColor = true;
    transparentMaskScene.clearedDepth = false;
    transparentMaskScene.clearedStencil = false;

    outlineScene.clearedColor = false;
    outlineScene.clearedDepth = false;
    outlineScene.clearedStencil = false;

    blendingScene.clearedColor = false;
    blendingScene.clearedDepth = false;
    blendingScene.clearedStencil = false;

    textScene.clearedColor = false;
    textScene.clearedDepth = false;
    textScene.clearedStencil = false;

    const screenChunk = primitiveFactory.create(primitiveType.SCREEN_QUAD);

    const opaqueLinesMaskShader = new ScreenShader({transparent : true, depthTest: false});
    opaqueLinesMaskShader.enableStencilTest();
    opaqueLinesMaskShader.setStencilFunction(stencilFunc.EQUAL);
    opaqueLinesMaskShader.setStencilReferenceValue(stencilValue.OPAQUE_LINES);
    const opaqueMaskScreenPlane = objFactory.createDrawable(opaqueLinesMaskShader);
    opaqueMaskScreenPlane.addGeometryChunk(screenChunk);
    opaqueMaskScreenPlane.setDiffuseColor(1.0, 1.0, 1.0);
    opaqueMaskScene.add(opaqueMaskScreenPlane);

    const transparentLinesMaskShader = new ScreenShader({transparent : true, depthTest: false});
    transparentLinesMaskShader.enableStencilTest();
    transparentLinesMaskShader.setStencilFunction(stencilFunc.EQUAL);
    transparentLinesMaskShader.setStencilReferenceValue(stencilValue.TRANSPARENT_LINES);
    const transparentMaskScreenPlane = objFactory.createDrawable(transparentLinesMaskShader);
    transparentMaskScreenPlane.addGeometryChunk(screenChunk);
    transparentMaskScreenPlane.setDiffuseColor(1.0, 1.0, 1.0);
    transparentMaskScene.add(transparentMaskScreenPlane);

    const width = 1 * screenUtils.getPixelRatio();
    const outlineShader = new OutlineShader({transparent : true,
      width: width,
      depthTest: false,
      outlineOnly: true});

    const opaqueHighlightScreenPlane = objFactory.createDrawable(outlineShader);
    opaqueHighlightScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    opaqueHighlightScreenPlane.setDiffuseColor(...OUTLINE_COLOR);
    opaqueHighlightScreenPlane.setOpacity(OPAQUE_OUTLINE_TRANSPARENCY);
    outlineScene.add(opaqueHighlightScreenPlane);

    const transparentHighlightScreenPlane = objFactory.createDrawable(outlineShader);
    transparentHighlightScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    transparentHighlightScreenPlane.setDiffuseColor(...OUTLINE_COLOR);
    transparentHighlightScreenPlane.setOpacity(TRANSPARENT_OUTLINE_TRANSPARENCY);
    outlineScene.add(transparentHighlightScreenPlane);

    const blendingShader = new CopyShader({transparent: true});
    const blendingScreenPlane = objFactory.createDrawable(blendingShader);
    blendingScreenPlane.addGeometryChunk(screenChunk);
    blendingScene.add(blendingScreenPlane);
  }

  update() {
    const opaqueLinesScene = this.getScenes()[0];
    const transparentLinesScene = this.getScenes()[1];
    const opaqueMaskScene = this.getScenes()[2];
    const transparentMaskScene = this.getScenes()[3];
    const outlineScene = this.getScenes()[4];
    const blendingScene = this.getScenes()[5];
    const textScene = this.getScenes()[6];

    const width = opaqueLinesScene.camera.viewport[2];
    const height = opaqueLinesScene.camera.viewport[3];

    const {drawFB, opaqueMaskFB, transparentMaskFB, blendingFB} = this.initFramebuffer(width, height);
    opaqueLinesScene.setFramebuffer(drawFB);
    transparentLinesScene.setFramebuffer(drawFB);
    opaqueMaskScene.setFramebuffer(opaqueMaskFB);
    transparentMaskScene.setFramebuffer(transparentMaskFB);
    outlineScene.setFramebuffer(blendingFB);
    blendingScene.setFramebuffer(blendingFB);
    textScene.setFramebuffer(blendingFB);

    const opaqueOutlineScreenPlane = outlineScene.getChildren()[0];
    const opaqueMaskTexture = opaqueMaskScene.getFramebuffer().colorAttachments[0];
    opaqueOutlineScreenPlane.setTexture(opaqueMaskTexture, 0);
    const transparentOutlineScreenPlane = outlineScene.getChildren()[1];
    const transparentMaskTexture = transparentMaskScene.getFramebuffer().colorAttachments[0];
    transparentOutlineScreenPlane.setTexture(transparentMaskTexture, 0);

    const blendingScreenPlane = this.getScreenQuad(blendingScene);
    const blendingTexture = transparentLinesScene.getFramebuffer().colorAttachments[0];
    blendingScreenPlane.setTexture(blendingTexture, 0);
  }

  initFramebuffer(width, height) {
    const colorTexture = this.attachmentsStorage[attachmentId.BASE_COLOR_TEXTURE];
    const drawTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA,
      {width, height});
    const opaqueMaskTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA,
      {width, height});
    const transparentMaskTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA,
      {width, height});
    const depthFormat = GL.checkWebGL2() ? RBIntFormat.D24S8 : RBIntFormat.DS;
    const depthStencil = resourceManager.initBuffer(pixelBufferType.RB_DEPTH,
      { width, height, format: depthFormat });

    const drawFB = new FrameBuffer([drawTexture], depthStencil);
    const opaqueMaskFB = new FrameBuffer([opaqueMaskTexture], depthStencil);
    const transparentMaskFB = new FrameBuffer([transparentMaskTexture], depthStencil);
    const blendingFB = new FrameBuffer([colorTexture], null);

    return {drawFB, opaqueMaskFB, transparentMaskFB, blendingFB};
  }

}
